<script setup lang="ts">
import { ref, watch } from 'vue';
import MileageCorrectionSearchForm from '@/components/MileageCorrection/MileageCorrectionSearchForm.vue';
import MileageCorrectionTable from '@/components/MileageCorrection/MileageCorrectionTable.vue';
import Api from '@/services/api/ApiServiceFabrick';
import { formatDate } from '@/services/functions';
import i18n from '@/lang/i18n';
import type getOperationOffice from '@/types/responses/getOperationOffice';
import type getDepartmentResponse from '@/types/responses/getDepartment';
import type DeliveryPlan from '@/types/DeliveryPlan';

const props = defineProps<{
  showFlash?: (message: string, type: string, fadeout: boolean) => void;
}>();

const MAX_DATE_RANGE = 31;

const selectedOperationOffice = ref<getOperationOffice | null>(null);
const selectedDepartment = ref<getDepartmentResponse | undefined>(undefined);
const selectedVehicle = ref<any | null>(null);
const operations = ref<DeliveryPlan[]>([]);
const loading = ref(false);
const saving = ref(false);
const currentPage = ref(1);
const totalPage = ref(0);
const itemsPerPage = ref(50);
const allElementsCount = ref(0);
const searched = ref(false);

const correctionBaseId = ref<string | null>(null);
const correctionOutOfServiceMeter = ref<number | null>(null);

function defaultDateRange(): Date[] {
  const today = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (MAX_DATE_RANGE - 1));
  return [from, today];
}

const dateRange = ref<Date[]>(defaultDateRange());

async function fetchOperations() {
  if (!selectedVehicle.value || !selectedOperationOffice.value) {
    return;
  }

  loading.value = true;
  operations.value = [];
  correctionBaseId.value = null;
  correctionOutOfServiceMeter.value = null;

  const daterange = `${formatDate(dateRange.value[0], '/')} - ${formatDate(dateRange.value[1], '/')}`;

  try {
    const res = await Api().getDailyReports({
      operation_office_id: selectedOperationOffice.value.id,
      car_id: selectedVehicle.value.id,
      driver_id: null,
      account_id: null,
      daterange,
      delivery_date: 1,
      return_date: 0,
      page: currentPage.value,
    });
    operations.value = res.items;
    totalPage.value = res.total_page;
    itemsPerPage.value = res.per_page;
    allElementsCount.value = res.total;
    searched.value = true;
  } finally {
    loading.value = false;
  }
}

function search() {
  currentPage.value = 1;
  operations.value = [];
  searched.value = false;
  fetchOperations();
}

function resetCorrection() {
  correctionBaseId.value = null;
  correctionOutOfServiceMeter.value = null;
}

watch(selectedVehicle, () => {
  operations.value = [];
  searched.value = false;
});

watch(currentPage, () => {
  if (selectedVehicle.value) {
    fetchOperations();
  }
});

async function save() {
  if (!correctionBaseId.value || correctionOutOfServiceMeter.value === null || !selectedVehicle.value) {
    return;
  }

  saving.value = true;

  try {
    await Api().applyMileageCorrection({
      delivery_plan_id: correctionBaseId.value,
      vehicle_id: selectedVehicle.value.id,
      out_of_service_meter: correctionOutOfServiceMeter.value,
      start_date: formatDate(dateRange.value[0], '/'),
      end_date: formatDate(dateRange.value[1], '/'),
    });
    props.showFlash?.(i18n.global.t('pages.common.messages.success'), 'success', true);
    await fetchOperations();
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="d-flex page-container">
    <div class="content p-4 w-100">
      <h4 class="card-title mt-0">{{ i18n.global.t('sideMenue.dailyReport.mileageCorrection') }}</h4>

      <div class="mr-3 ml-3 content-body">
        <MileageCorrectionSearchForm
          v-model:selectedOperationOffice="selectedOperationOffice"
          v-model:selectedDepartment="selectedDepartment"
          v-model:selectedVehicle="selectedVehicle"
          v-model:dateRange="dateRange"
          :loading="loading"
          :maxDateRange="MAX_DATE_RANGE"
          @search="search"
        />

        <MileageCorrectionTable
          v-if="selectedVehicle && searched"
          :trips="operations"
          :loading="loading"
          :totalPage="totalPage"
          :itemsPerPage="itemsPerPage"
          :allElementsCount="allElementsCount"
          v-model:currentPage="currentPage"
          v-model:correctionBaseId="correctionBaseId"
          v-model:correctionOutOfServiceMeter="correctionOutOfServiceMeter"
          class="mt-4"
        />

        <div v-if="selectedVehicle && searched" class="d-flex justify-content-end mt-3">
          <v-btn class="ma-2 reset-btn" :disabled="correctionBaseId === null" @click="resetCorrection">
            {{ i18n.global.t('pages.common.button.reset') }}
          </v-btn>
          <v-btn class="ma-2 exec-btn" :disabled="correctionBaseId === null" :loading="saving" @click="save">
            {{ i18n.global.t('pages.common.button.conservation') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-container {
  min-height: 45vw;
}
.content-body {
  overflow-x: hidden;
}
</style>
